import { useEffect, useRef, useState } from "react"
import { useSearchParams } from "react-router-dom"
import { useQueryClient } from "@tanstack/react-query"
import type { AxiosResponse } from "axios"
import { Input } from "components/ui/inputs/Input"
import { useUpdateEref } from "hooks/api/quotation/quotations.hook"
import { useApiErrorFormatter } from "hooks/apiErrorFormatter.hook"
import { applyDisconnectedEcallsToCache, spliceErefIntoCache } from "helpers/quotationCache.helper"
import { useEntityMutationQueue } from "hooks/mutationQueue.hook"
import { ErefNotReferencedIndicator } from "components/ui/customWebviewer/shared/ErefNotReferencedIndicator/ErefNotReferencedIndicator"
import { isErefNotReferenced } from "components/ui/customWebviewer/shared/ErefNotReferencedIndicator/erefNotReferenced.helpers"
import { erefQueueKey } from "components/ui/customWebviewer/ReferenceSidebar/erefQueueKey"
import type { TErefWithFurniture } from "types/elevationReferences.type"
import type { TQuotationDetail } from "types/quotations.type"
import style from "./TimesReferenced.module.scss"

const { wrapper, label, field, inputWrapper, input, error } = style

/** The endpoint's own bounds for `times_referenced`: integer, `minimum: 0`, `maximum: 99`. */
const MIN_TIMES_REFERENCED = 0
const MAX_TIMES_REFERENCED = 99
const VALIDATION_MESSAGE = `Enter a whole number from ${MIN_TIMES_REFERENCED} to ${MAX_TIMES_REFERENCED}`

/**
 * Digits only. Stopping the value at the source (as `CreateErefSidebar` does for the elevation
 * number's length) is what makes a negative or fractional entry unreachable by typing, leaving
 * "empty" and "above 99" as the only states validation has to speak about.
 */
const toDigits = (value: string) => value.replace(/\D/g, "")

type Props = { eref: TErefWithFurniture }

/**
 * The eref's `Times Referenced` field (REQ-423) — the multiplier the estimator can correct when a
 * callout is missing from the drawing or cannot be detected graphically, so the connected-ecall
 * count understates reality.
 *
 * It is a plain text input rather than an `InputNumber` on purpose: Ant silently clamps an
 * out-of-range value on blur, and the requirement asks for validation *feedback* instead. An invalid
 * value therefore stays on screen with its message and sends nothing, so the field is never saved
 * empty or out of range.
 *
 * It also shows **no saving skeleton**, unlike `NumberAndPage` next to it. Swapping the input out
 * mid-save hides the number the user just typed — which is the feedback — and drops focus to the
 * document, so keystrokes right after Enter are lost (both measured in the browser). Skipping it also
 * removes the question the skeleton drags along: a pending flag is either per-mutation-instance or
 * per-eref-queue, and each scope blanks this field for a save it does not belong to. Nothing is lost
 * for a two-digit field that commits in well under a second: the typed value is already on screen,
 * and a failure surfaces as a toast.
 */
export const TimesReferenced = ({ eref }: Props) => {
	const [searchParams] = useSearchParams()
	const quotationId = Number(searchParams.get("quotationID"))
	const queryClient = useQueryClient()
	const { formatError } = useApiErrorFormatter()
	const { mutateAsync: updateErefAsync } = useUpdateEref()
	// Same key as `NumberAndPage` and the Configuration tab: one queue and one `versionRef` per eref,
	// so concurrent edits of different fields can't collide on the optimistic-locking version.
	const { enqueue, versionRef } = useEntityMutationQueue(erefQueueKey(eref.id), eref.version)

	const serverValue = eref.times_referenced ?? MIN_TIMES_REFERENCED
	const savedRef = useRef(serverValue)
	// Value in flight, so committing with Enter and then blurring doesn't PATCH the same number twice.
	const pendingRef = useRef<number | null>(null)
	const [value, setValue] = useState(String(serverValue))

	// `eref.id` is in the deps, not just the value: an invalid entry never commits, so selecting
	// another eref that happens to carry the same count would otherwise leave the rejected text — and
	// its error — sitting in the new eref's field.
	useEffect(() => {
		savedRef.current = serverValue
		pendingRef.current = null
		setValue(String(serverValue))
	}, [eref.id, serverValue])

	const parsed = value === "" ? null : Number(value)
	const isValid = parsed !== null && parsed >= MIN_TIMES_REFERENCED && parsed <= MAX_TIMES_REFERENCED

	const saveHandle = () => {
		if (parsed === null || !isValid) return
		const next = parsed
		// Canonical form, so "03" settles as "3" rather than looking like an unsaved edit.
		setValue(String(next))
		if (next === savedRef.current || next === pendingRef.current) return

		pendingRef.current = next
		enqueue(async () => {
			try {
				const response = await updateErefAsync({
					quotationId,
					erefId: eref.id,
					body: { version: versionRef.current, times_referenced: next },
				})
				const updated = response?.data
				if (!updated) return
				const { disconnected_ecalls = [], disconnected_ecall_elevations = [], ...erefData } = updated
				versionRef.current = erefData.version
				savedRef.current = erefData.times_referenced ?? next
				queryClient.setQueryData<AxiosResponse<TQuotationDetail> | undefined>(["quotation", quotationId], (cached) => {
					const nextCache = spliceErefIntoCache(cached, erefData)
					return applyDisconnectedEcallsToCache(nextCache, disconnected_ecalls, disconnected_ecall_elevations)
				})
			} catch (e) {
				formatError(e)
				queryClient.invalidateQueries({ queryKey: ["quotation", quotationId] })
			} finally {
				pendingRef.current = null
			}
		})
	}

	// No saving skeleton, deliberately — see the header comment.
	return (
		<div className={wrapper}>
			<span className={label}>Times referenced</span>
			<div className={field}>
				<div className={inputWrapper}>
					<Input
						className={input}
						value={value}
						status={isValid ? undefined : "error"}
						onChange={(e) => setValue(toDigits(e.target.value))}
						onBlur={saveHandle}
						onPressEnter={saveHandle}
					/>
				</div>
				{/* Follows the typed value (REQ-464), so it clears as soon as the user types a count. */}
				{isValid && isErefNotReferenced(parsed) ? <ErefNotReferencedIndicator /> : null}
			</div>
			{isValid ? null : <p className={error}>{VALIDATION_MESSAGE}</p>}
		</div>
	)
}
